/**
 * Project approval bottom sheet (Phase 3).
 *
 * Renders the 3-stage approval state for a project on the new flow:
 *   stepper → BU Head → Dept Head → Finance (L1/L2/L3 by amount)
 *
 * Visible action buttons depend on the viewer's permission for the current
 * stage. Dept Head and Finance stages support approve-with-changes (the
 * viewer can edit the amount before approving).
 */

import { FunctionComponent, useEffect, useState } from "react";
import StepperWidget from "../../../../ui/new_components/common/stepper_widget";
import { useAuth } from "../../../../context_providers/auth/authProvider";
import { ACTION, MODULE } from "../../../../utils/enums";
import {
  Project,
  ProjectApprovalStatus,
  project_approval_level,
  project_approval_threshold,
  project_levelModule,
} from "../../models/project_model";

interface Props {
  data: Project;
  onSubmit: (payload: {
    approval_status: string;
    level: string;
    modified_amount?: number;
    reason?: string;
  }) => void;
}

const ProjectApprovalBottomSheet: FunctionComponent<Props> = (props) => {
  const { isAuthorised } = useAuth();
  const [canActAtCurrent, setCanActAtCurrent] = useState(false);
  const [modifiedAmount, setModifiedAmount] = useState<number | "">("");

  useEffect(() => {
    const currentModule =
      project_levelModule[props.data.current_approval_level || ""];
    setCanActAtCurrent(
      !!currentModule &&
        isAuthorised({ module: currentModule, action: ACTION.UPDATE }),
    );
    setModifiedAmount("");
  }, [props.data.current_approval_level, isAuthorised]);

  const rule = project_approval_threshold[props.data.approval_level || ""];
  const steps = rule
    ? rule.approvals_required.map((r) => r.level)
    : [];
  const activeIdx = steps.indexOf(props.data.current_approval_level || "");
  const isFinalised =
    props.data.approvalStatus === ProjectApprovalStatus.approved ||
    props.data.approvalStatus === ProjectApprovalStatus.rejected;

  const currentLevel = props.data.current_approval_level || "";
  const canModifyAmount =
    currentLevel === project_approval_level.dept_head ||
    currentLevel === project_approval_level.finance_L1 ||
    currentLevel === project_approval_level.finance_L2 ||
    currentLevel === project_approval_level.finance_L3;

  if (!rule) return null;

  return (
    <div className="mt-2 border rounded-md p-3 bg-gray-50">
      <div className="pb-5">
        <StepperWidget
          steps={steps.map((label, i) => ({
            label,
            validated:
              i < activeIdx ||
              (isFinalised &&
                props.data.approvalStatus === ProjectApprovalStatus.approved),
            required: false,
          }))}
          activeStep={activeIdx < 0 ? 0 : activeIdx}
          onChange={() => {}}
        />
      </div>

      <div className="flex flex-col md:flex-row md:items-end gap-3 justify-between">
        <div className="text-xs text-gray-500">
          Approval level: <b>{props.data.approval_level}</b>
          {" · "}
          Current stage: <b>{props.data.current_approval_level || "—"}</b>
          {props.data.original_budget !== undefined &&
            props.data.original_budget !== props.data.budget && (
              <span className="ml-2 text-amber-700">
                (originally requested ₹{props.data.original_budget?.toLocaleString()})
              </span>
            )}
        </div>

        {canActAtCurrent && !isFinalised && (
          <div className="flex items-end gap-3 text-sm">
            {canModifyAmount && (
              <div className="flex flex-col">
                <label className="text-xs text-gray-600">
                  Modify amount (optional)
                </label>
                <input
                  type="number"
                  data-testid="proj-modify-amount"
                  placeholder={String(props.data.budget ?? "")}
                  value={modifiedAmount}
                  onChange={(e) =>
                    setModifiedAmount(
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                  className="border rounded px-2 py-1 w-40"
                />
              </div>
            )}
            <button
              onClick={() => {
                const reason =
                  window.prompt("Reason for rejection?")?.trim() || "";
                if (!reason) return;
                if (
                  !window.confirm(
                    `Reject this project at ${currentLevel}?`,
                  )
                )
                  return;
                props.onSubmit({
                  approval_status: ProjectApprovalStatus.rejected,
                  level: currentLevel,
                  reason,
                });
              }}
              className="rounded-md border border-myPrimaryColor px-3 py-1 text-myPrimaryColor"
            >
              Reject
            </button>
            <button
              onClick={() => {
                const payload: {
                  approval_status: string;
                  level: string;
                  modified_amount?: number;
                } = {
                  approval_status: ProjectApprovalStatus.approved,
                  level: currentLevel,
                };
                if (
                  canModifyAmount &&
                  modifiedAmount !== "" &&
                  Number(modifiedAmount) !== props.data.budget
                ) {
                  payload.modified_amount = Number(modifiedAmount);
                }
                const confirmMsg = payload.modified_amount
                  ? `Approve at ${currentLevel} with amount changed to ₹${payload.modified_amount}?`
                  : `Approve at ${currentLevel}?`;
                if (!window.confirm(confirmMsg)) return;
                props.onSubmit(payload);
              }}
              className="bg-myPrimaryColor border border-myPrimaryColor rounded-md px-3 py-1 text-white"
            >
              {modifiedAmount !== "" &&
              canModifyAmount &&
              Number(modifiedAmount) !== props.data.budget
                ? "Approve with changes"
                : "Approve"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectApprovalBottomSheet;
